import type { TermsDocument } from '../types';

export const privacyPolicyDocument: TermsDocument = {
  version: 2,
  lastUpdated: '8 April 2026',
  title: 'Privacy Policy',
  sections: [
    {
      title: '',
      content: [
        {
          type: 'text',
          text: 'Careerbook Enterprise Ltd ("Company", "we", "our", or "us") respects your privacy and is committed to protecting the personal data you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website www.careerbook.co.uk (the "Site") or use our services.',
        },
      ],
    },
    {
      title: 'Definitions and Key Terms',
      content: [
        {
          type: 'bulleted',
          items: [
            'Company: Careerbook Enterprise Ltd, 5 Wingate Close, St Leonards On Sea, East Sussex, United Kingdom, TN38 0SD',
            'Service: The Careerbook website and platform accessed via www.careerbook.co.uk',
            'User: Anyone who accesses or uses the Service',
            'Personal Data: Any information relating to an identified or identifiable natural person',
            'Cookies: Small files stored on your device to improve site functionality',
          ],
        },
      ],
    },
    {
      title: 'Information We Collect',
      content: [
        {
          type: 'text',
          text: 'We collect the following types of information:',
        },
        {
          type: 'bulleted',
          items: [
            'Personal Identification Information: Name, email address, phone number, job title, institution, and payment information',
            'Usage Data: Browser type, device information, IP address, pages visited, access time, and referring URLs',
            'Cookies: Session cookies, preference cookies, and security cookies',
            'Event and Platform Data: Event bookings, attendance records, feedback responses, certificates issued, and group or institutional membership where applicable',
          ],
        },
      ],
    },
    {
      title: 'How We Use Your Information',
      content: [
        {
          type: 'text',
          text: 'We use collected data to:',
        },
        {
          type: 'bulleted',
          items: [
            'Provide and maintain our services',
            'Manage user accounts',
            'Process payments',
            'Communicate with you',
            'Send marketing and promotional content (if consented)',
            'Monitor usage and analyse trends',
            'Support educational governance, training delivery, and attendance monitoring',
            'Comply with legal obligations',
          ],
        },
      ],
    },
    {
      title: 'Legal Basis for Processing',
      content: [
        {
          type: 'text',
          text: 'We process your personal data based on:',
        },
        {
          type: 'bulleted',
          items: [
            'Legitimate interests (primary basis): where processing is necessary to provide our services, including managing events, attendance tracking, feedback collection, and supporting educational governance and training requirements. We ensure this processing is necessary, proportionate, and does not override your rights.',
            'Your consent: where required (for example, marketing communications)',
            'The performance of a contract: where processing is necessary to deliver services you have requested',
            'Legal obligations: where required to comply with applicable laws and regulations',
          ],
        },
      ],
    },
    {
      title: 'Important Information Regarding Free-Text Inputs',
      content: [
        {
          type: 'text',
          text: 'Some areas of the platform (such as feedback forms) allow users to enter free-text responses.',
        },
        {
          type: 'text',
          text: 'Users should not include personal, confidential, or sensitive information (such as health data or personal circumstances) in these fields.',
        },
        {
          type: 'text',
          text: 'We do not request or require such information. If such data is submitted, it may be reviewed and removed by authorised administrators where appropriate.',
        },
      ],
    },
    {
      title: 'Sharing Your Information',
      content: [
        {
          type: 'text',
          text: 'We may share your data with:',
        },
        {
          type: 'bulleted',
          items: [
            'NHS Trusts and education providers (for training, attendance, and governance purposes)',
            'Third-party service providers (e.g. hosting, payment processors, email delivery)',
            'Law enforcement where required by law',
          ],
        },
        {
          type: 'text',
          text: 'We do not sell or rent your personal information to third parties.',
        },
      ],
    },
    {
      title: 'Security of Your Information',
      content: [
        {
          type: 'text',
          text: 'We use administrative, technical, and physical security measures to protect your personal data. These include:',
        },
        {
          type: 'bulleted',
          items: [
            'Secure cloud hosting using Amazon Web Services (AWS)',
            'Encryption in transit (TLS 1.2+)',
            'Encryption at rest (AES-256)',
            'Role-based access controls',
          ],
        },
        {
          type: 'text',
          text: 'While we strive to use commercially acceptable means to protect your data, we cannot guarantee absolute security.',
        },
      ],
    },
    {
      title: 'Data Hosting and Location',
      content: [
        {
          type: 'text',
          text: 'Your data is hosted and processed within the United Kingdom using Amazon Web Services (AWS) London region.',
        },
        {
          type: 'text',
          text: 'We do not routinely transfer personal data outside of the UK.',
        },
      ],
    },
    {
      title: 'Your Data Protection Rights',
      content: [
        {
          type: 'text',
          text: 'You have the right to:',
        },
        {
          type: 'bulleted',
          items: [
            'Access, update or delete your information',
            'Withdraw consent at any time (where consent is relied upon)',
            'Object to or restrict our processing',
            'Data portability',
            "Lodge a complaint with the Information Commissioner's Office (ICO)",
          ],
        },
        {
          type: 'text',
          text: 'To exercise any of these rights, contact us at info@careerbook.co.uk.',
        },
      ],
    },
    {
      title: 'Retention of Data',
      content: [
        {
          type: 'text',
          text: 'We retain personal data as long as necessary to:',
        },
        {
          type: 'bulleted',
          items: [
            'Fulfil the purposes outlined in this policy',
            'Comply with legal obligations',
            'Resolve disputes',
            'Enforce agreements',
          ],
        },
        {
          type: 'text',
          text: 'Where applicable:',
        },
        {
          type: 'bulleted',
          items: [
            'User account data is retained until account deletion or termination',
            'Event and institutional data may be retained for a defined period following completion (for example, for reporting and governance purposes)',
            'Data may be deleted at any time upon request by the user or organisation',
          ],
        },
      ],
    },
    {
      title: 'Cookies and Similar Technologies',
      content: [
        {
          type: 'text',
          text: 'We use cookies and similar technologies to recognise you when you visit our website at www.careerbook.co.uk. Cookies are small text files that a website stores on your device when you visit. They help the site remember your preferences (such as login, language, and other settings) over time so you do not have to keep re-entering them whenever you return to the site or browse from one page to another.',
        },
        {
          type: 'text',
          text: 'We use cookies for a variety of purposes, including to:',
        },
        {
          type: 'bulleted',
          items: [
            'Recognise users and track site usage',
            'Store user preferences',
            'Improve functionality',
            'Facilitate login and secure authentication',
            'Analyse site traffic and trends',
            'Deliver targeted advertising or content',
          ],
        },
        {
          type: 'text',
          text: 'Cookies help us understand how our services are being used, personalise content, and improve user experience.',
        },
      ],
    },
    {
      title: 'Cookie Types We Use',
      content: [
        {
          type: 'text',
          text: 'Below is a list of the main types of cookies we use:',
        },
        {
          type: 'bulleted',
          items: [
            'Essential Cookies: These are necessary for the website to function. Without them, services like account login or secure payment cannot be provided.',
            'Performance & Analytics Cookies: These collect information about how users interact with our website, such as pages visited and links clicked, helping us improve site performance.',
            'Functionality Cookies: These remember your choices (such as username or language) and provide enhanced, more personalised features.',
            'Targeting/Advertising Cookies: These cookies track your browsing habits to deliver more relevant advertisements based on your interests.',
          ],
        },
      ],
    },
    {
      title: 'Third-Party Cookies and Providers',
      content: [
        {
          type: 'text',
          text: 'We may use third-party services such as Google Analytics, Meta (Facebook), or other service providers who may set their own cookies to:',
        },
        {
          type: 'bulleted',
          items: [
            'Analyse usage patterns',
            'Display relevant content or ads on other sites',
            'Measure the effectiveness of marketing campaigns',
          ],
        },
        {
          type: 'text',
          text: 'We do not control these third-party cookies. For more information, please refer to the respective privacy policies of these providers.',
        },
      ],
    },
    {
      title: 'Cookie Consent and Browser Controls',
      content: [
        {
          type: 'text',
          text: 'When you first visit our website, you will be presented with a cookie banner requesting your consent to the use of non-essential cookies. By clicking "Accept All" or continuing to use the site, you consent to our use of cookies.',
        },
        {
          type: 'text',
          text: 'You can change your preferences or withdraw your consent at any time by visiting our Cookie Settings link at the bottom of the website.',
        },
        {
          type: 'text',
          text: 'Most browsers allow you to control cookies through settings. You can:',
        },
        {
          type: 'bulleted',
          items: [
            'View, delete, or block cookies',
            'Set your browser to alert you before a cookie is placed',
            'Disable all cookies completely',
          ],
        },
        {
          type: 'text',
          text: 'Note: Disabling cookies may affect the functionality of our website.',
        },
        {
          type: 'text',
          text: 'To manage cookies, refer to the support pages for your browser: Google Chrome, Mozilla Firefox, Safari, Microsoft Edge.',
        },
        {
          type: 'text',
          text: 'You may disable cookies in your browser, but some parts of the site may not function correctly.',
        },
      ],
    },
    {
      title: 'Third-Party Links',
      content: [
        {
          type: 'text',
          text: 'Our website may contain links to external websites. We are not responsible for the privacy practices or content of such third-party sites.',
        },
      ],
    },
    {
      title: 'International Transfers',
      content: [
        {
          type: 'text',
          text: 'If your data is transferred outside the UK, we ensure that appropriate safeguards (such as Standard Contractual Clauses) are in place.',
        },
      ],
    },
    {
      title: "Children's Privacy",
      content: [
        {
          type: 'text',
          text: 'Our services are not intended for individuals under the age of 16. We do not knowingly collect data from children.',
        },
      ],
    },
    {
      title: 'Changes to This Privacy Policy',
      content: [
        {
          type: 'text',
          text: 'We may update this Privacy Policy from time to time. We will notify users of significant changes via email or notice on the website.',
        },
      ],
    },
    {
      title: 'Contact Us',
      content: [
        {
          type: 'text',
          text: 'If you have any questions about this Privacy Policy, please contact:\n',
        },
        {
          type: 'text',
          text: 'Careerbook Enterprise Ltd',
        },
        {
          type: 'text',
          text: '5 Wingate Close',
        },
        {
          type: 'text',
          text: 'St Leonards On Sea',
        },
        {
          type: 'text',
          text: 'East Sussex',
        },
        {
          type: 'text',
          text: 'United Kingdom',
        },
        {
          type: 'text',
          text: 'TN38 0SD',
        },
        {
          type: 'text',
          text: 'Email: info@careerbook.co.uk',
        },
      ],
    },
  ],
};
